// ═══════════════════════════════════════════════════════════════════════════
// El guardia de la administración de plataforma
//
// El operador: "Debe haber una página de administración independiente de
// Vinícola Total. Es otro nivel de la administración."
//
// ── POR QUÉ NO SIRVE EL GUARDIA DE SIEMPRE ─────────────────────────────────
//
// `Guardia` exige MEMBRESÍA: si entras y no tienes ninguna, te manda a canjear
// un ticket. Correcto para quien trabaja en una vinícola, e imposible para
// quien administra la plataforma — porque D12 dice justamente que el
// superusuario NO debe tener membresía en ninguna, para que no pueda curiosear
// los datos de nadie.
//
// Hoy eso no se nota porque en las dos bases el administrador de plataforma es
// ADEMÁS dueño de una vinícola:
//
//     producción  bbf2d251…  1 membresía   ← entra por eso
//     pruebas     23383516…  1 membresía   ← entra por eso
//
// Quítale esa membresía —que es exactamente lo que significa «independiente de
// Vinícola Total»— y no podría ni iniciar sesión. Este guardia es lo que
// vuelve posible un superusuario puro.
//
// ── LO QUE COMPRUEBA, Y DÓNDE VIVE LA REGLA ────────────────────────────────
//
// Pregunta `es_super_admin()` a la base y nada más. No mira roles, ni
// membresías, ni listas locales: la autoridad es la tabla `platform_admin`,
// consultada con la sesión de quien llama. Si un día alguien sirviera esta
// página desde otro sitio, seguiría sin poder hacer nada — porque todas las
// funciones de plataforma vuelven a comprobar lo mismo del lado del servidor.
//
// Esta pantalla no es la seguridad. Es la cortesía de no enseñar una consola
// vacía a quien no va a poder usarla.
// ═══════════════════════════════════════════════════════════════════════════

import React, { useState, useEffect } from "react";
import { supabase, sesionLocal, alCambiarSesion, salir } from "./sesion.js";
import { Login } from "./Login.jsx";
import { nombreEntorno, esProduccion } from "./config.js";

export function GuardiaPlataforma({ children }) {
  const [estado, setEstado] = useState("cargando");
  const [sesion, setSesion] = useState(null);

  useEffect(() => {
    let vivo = true;

    const revisar = async (s) => {
      if (!vivo) return;
      if (!s) { setSesion(null); setEstado("fuera"); return; }
      setSesion(s);
      try {
        const { data, error } = await supabase.rpc("es_super_admin");
        if (!vivo) return;
        if (error) throw error;
        setEstado(data ? "dentro" : "ajeno");
      } catch (e) {
        if (!vivo) return;
        // Un fallo de red NO es un «no eres superusuario». Decirlo así mandaría
        // a alguien a pedir permisos que ya tiene.
        console.warn("[plataforma] no se pudo comprobar el permiso:", e?.message);
        setEstado("sin-red");
      }
    };

    sesionLocal().then(revisar);
    const desuscribir = alCambiarSesion((s) => revisar(s));
    return () => { vivo = false; desuscribir?.(); };
  }, []);

  if (estado === "cargando") return <Centrado>Comprobando el acceso…</Centrado>;

  if (estado === "fuera") {
    return (
      <div>
        <Cintillo />
        {/* El MISMO login de la app de vinícola: no hay dos formas de entrar,
            y una copia se habría quedado atrás el día que cambie la política
            de contraseñas. Aquí no se ofrece canjear ticket —un ticket da
            acceso a UNA vinícola, no a la plataforma— así que no se le pasa. */}
        <Login onEntrar={({ session }) => { setSesion(session); setEstado("cargando"); }} />
      </div>
    );
  }

  if (estado === "ajeno") {
    return (
      <Centrado>
        <div style={{ fontSize: 15, color: "#e8e0d4", marginBottom: 10 }}>
          Esta cuenta no administra la plataforma
        </div>
        <div style={{ fontSize: 13, color: "#888", lineHeight: 1.7, maxWidth: 420 }}>
          Entraste como <b style={{ color: "#aaa" }}>{sesion?.user?.email}</b>, que no
          está en la administración de plataforma. Si querías entrar a tu vinícola,
          esta no es la dirección: esta consola administra vinícolas, no opera dentro
          de ninguna.
        </div>
        <button onClick={() => salir()} style={BOTON}>Salir</button>
      </Centrado>
    );
  }

  if (estado === "sin-red") {
    return (
      <Centrado>
        <div style={{ fontSize: 15, color: "#facc15", marginBottom: 10 }}>
          No se pudo comprobar el acceso
        </div>
        <div style={{ fontSize: 13, color: "#888", lineHeight: 1.7, maxWidth: 420 }}>
          El servidor no contestó. No significa que no tengas permiso — significa
          que no se pudo preguntar.
        </div>
        <button onClick={() => window.location.reload()} style={BOTON}>Reintentar</button>
      </Centrado>
    );
  }

  return children({ sesion });
}

/** Aviso permanente de en qué base se está trabajando. */
export function Cintillo() {
  if (esProduccion()) return null;
  return (
    <div style={{ background: "#7a1f6a", color: "#fff", textAlign: "center",
                  padding: "4px 8px", fontSize: 11, letterSpacing: 1 }}>
      {nombreEntorno().toUpperCase()} — esta consola administra la base de {nombreEntorno()}
    </div>
  );
}

const BOTON = {
  marginTop: 18, padding: "9px 20px", borderRadius: 7,
  border: "1px solid #333", background: "#1a1a1a", color: "#c9a96e",
  cursor: "pointer", fontSize: 13,
};

function Centrado({ children }) {
  return (
    <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column",
                  alignItems: "center", justifyContent: "center", gap: 4,
                  padding: 24, textAlign: "center", background: "#0a0a0a",
                  color: "#888", fontFamily: "'DM Sans', system-ui, sans-serif" }}>
      {children}
    </div>
  );
}
